// AI Visibility dashboard data (SGL-140).
// Calls chat-service's Cognito-authed proxy — the geo-visibility X-Sagelyn-Key
// is a server secret and must never reach the browser, so the frontend only
// ever talks to /visibility/* on chat-service.
import { authedFetch } from "../auth/authedFetch";
import { CHAT_API_BASE_URL } from "./chatService";

export interface VisibilityRow {
  key: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface VisibilitySummary {
  clicks: number;
  impressions: number;
  ctr: number;
  avg_position: number;
}

export interface SearchPerformance {
  connected: boolean;
  reason?: "no_website_on_profile" | "no_data_for_property";
  site: string | null;
  date_range?: { start: string; end: string };
  summary?: VisibilitySummary;
  queries?: VisibilityRow[];
  pages?: VisibilityRow[];
  opportunities?: VisibilityRow[];
  /**
   * Bing Webmaster Tools beside the Google figures (SGL-134). Optional because a
   * response from before SGL-134 has neither field; present on every response
   * (connected or not) once it ships.
   */
  bing?: BingSearchPerformance;
  /** Only computed when both engines were measured; empty otherwise. */
  disagreements?: SearchDisagreement[];
}

/* ── Bing Webmaster Tools (SGL-134) ────────────────────────────────────────
 * Mirrors chat-service app/routes/visibility.py (_bing_search_performance,
 * _bing_unmeasured, _bing_index, _disagreements). */

/**
 * ok               — measured; every field below is filled
 * partial          — measured, but Bing failed some datasets (`failed_datasets`);
 *                    each of those is null, which means unknown, never zero
 * not_connected    — no Bing site on this Brand
 * needs_reauth     — connected before API keys (OAuth); connect again with a key
 * key_invalid      — Bing refuses the stored API key (deleted or regenerated)
 * bing_unavailable — Bing itself didn't answer; the connection is fine
 * error            — we could not read it; `error` says why. NEVER "no data".
 */
export type BingStatus =
  | "ok"
  | "partial"
  | "not_connected"
  | "needs_reauth"
  | "key_invalid"
  | "bing_unavailable"
  | "error";

/** The four reads one Bing pull makes; a partial pull names the ones that failed. */
export type BingDataset = "query" | "page" | "crawl" | "crawl_issues";

/** The owner's reminder to replace their API key (social-management sets rotate_by,
 *  90 days after the key was saved). Absent when not connected. */
interface BingKeyRotation {
  /** Epoch seconds; null for a connection from before API keys. */
  rotate_by?: number | null;
  /** True from rotate_by on. The key still works; it is a reminder only. */
  rotation_due?: boolean;
}

/** Summed over `crawl_days` daily rows. `null` = no day reported that counter,
 *  which is not the same as zero errors. */
export interface BingCrawlErrors {
  code_4xx: number | null;
  code_5xx: number | null;
  blocked_by_robots: number | null;
  dns_failures: number | null;
  connection_timeout: number | null;
}

export interface BingCrawlIssue {
  url: string;
  http_code: number | null;
  /** Passed through as Bing returns it; its encoding is UNVERIFIED (flags int or names). */
  issues: unknown;
}

/** The crawl fields are all null when the "crawl" dataset failed, the issue fields
 *  when "crawl_issues" did (a partial pull). */
export interface BingIndexHealth {
  /** Pages in Bing's index on the newest day reported; null when none was. */
  in_index: number | null;
  in_index_trend: { date: string; in_index: number }[] | null;
  crawl_errors: BingCrawlErrors | null;
  /** How many daily rows crawl_errors sums — the basis travels with the number. */
  crawl_days: number | null;
  /** Capped list; crawl_issues_total says how many there are in all. */
  crawl_issues: BingCrawlIssue[] | null;
  crawl_issues_total: number | null;
}

interface BingMeasured extends BingKeyRotation {
  status: "ok" | "partial";
  site: string;
  /** Empty when ok. Each dataset named here came back null. */
  failed_datasets: BingDataset[];
  date_range: { start: string | null; end: string | null };
  /** Newest day Bing actually had data for; its lag is not documented. */
  data_through: string | null;
  /** Null when the "query" dataset failed. */
  summary: VisibilitySummary | null;
  /** `position` 0 means Bing reported no position for that row, not rank 0. */
  queries: VisibilityRow[] | null;
  pages: VisibilityRow[] | null;
  index: BingIndexHealth;
}

/** Every unmeasured status carries nulls, never empty lists or zeros. */
interface BingUnmeasured extends BingKeyRotation {
  status: Exclude<BingStatus, "ok" | "partial">;
  site: string | null;
  /** Reason code, only when status is "error". */
  error?: string;
  date_range: null;
  summary: null;
  queries: null;
  pages: null;
  index: null;
}

export type BingSearchPerformance = BingMeasured | BingUnmeasured;

export type DisagreementType =
  | "bing_only_query"
  | "google_only_query"
  | "position_gap"
  | "google_only_page"
  | "bing_only_page";

export interface SearchDisagreement {
  type: DisagreementType;
  key: string;
  google: VisibilityRow | null;
  bing: VisibilityRow | null;
}

export async function fetchSearchPerformance(
  customerId: string,
  idToken: string,
  siteUrl?: string,
): Promise<SearchPerformance> {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  const base = CHAT_API_BASE_URL.replace(/\/$/, "");
  let url = `${base}/visibility/search-performance?customer_id=${encodeURIComponent(customerId)}`;
  if (siteUrl) url += `&site_url=${encodeURIComponent(siteUrl)}`;
  const resp = await authedFetch(url, {}, idToken);
  if (!resp.ok) {
    throw new Error(`Visibility API error (${resp.status})`);
  }
  return resp.json();
}

/* ── Weekly Search Console comparison (SGL-470) ─────────────────────────── */

export type WeeklySearchStatus =
  | "ok"
  | "insufficient_data"
  | "not_connected"
  | "property_mismatch"
  | "needs_reauth"
  | "error";

export interface WeeklyMetric {
  this: number;
  last: number;
  delta_pct?: number | null;
}

export interface WeeklyStriking {
  query: string;
  position: number;
  impressions: number;
  clicks: number;
  page: string | null;
}

export interface WeeklyPageChange {
  page: string;
  clicks: WeeklyMetric;
  impressions: WeeklyMetric;
}

export interface WeeklySearch {
  status: WeeklySearchStatus;
  reason?: string;
  site?: string | null;
  windows: { this: [string, string]; last: [string, string] };
  totals?: Record<"clicks" | "impressions" | "ctr" | "position", WeeklyMetric>;
  striking?: WeeklyStriking[];
  drops?: WeeklyPageChange[];
  winners?: WeeklyPageChange[];
}

export async function fetchWeeklySearch(customerId: string, idToken: string): Promise<WeeklySearch> {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  const base = CHAT_API_BASE_URL.replace(/\/$/, "");
  const url = `${base}/visibility/search-opportunities?customer_id=${encodeURIComponent(customerId)}`;
  const resp = await authedFetch(url, {}, idToken);
  if (!resp.ok) {
    throw new Error(`Visibility API error (${resp.status})`);
  }
  return resp.json();
}

/* ── AI mention check (SGL-135) ─────────────────────────────────────────── */

/** How a business appears in one engine's answer to one question. */
export type MentionRole =
  | "recommended"
  | "mentioned"
  | "cited_source_only"
  | "absent"
  // No finding at all — the engine or the classifier failed for this cell.
  // Distinct from "absent", which is a measured result.
  | "unknown";

/**
 * Where a buyer question came from (SGL-178).
 *
 * "search_console" — the customer's own real search queries.
 * "template"       — our standard set for their category.
 * "generated"      — written from the services on their Brand profile. A guess
 *                    at what buyers ask, not measured demand.
 *
 * Every non-"search_console" source MUST be labelled in the UI: presenting a
 * guessed question as measured demand is the defect SGL-178 exists to remove.
 */
export type PromptSource = "search_console" | "template" | "generated";

/**
 * Why the questions are not the customer's real Search Console searches.
 * Stable codes from the backend: the UI picks its own words, because "connect
 * Search Console" is wrong advice for someone who already has.
 *
 * null/undefined = the run predates reasons; "" = real questions.
 */
export type PromptSourceReason =
  | "no_brand" | "no_website" | "not_connected" | "needs_reauth" | "property_mismatch"
  | "no_data" | "too_few_queries" | "pull_failed" | "lookup_failed";

export interface MentionMatrixRow {
  question: string;
  /** 1 = buying intent ("best plumber in X"), 2 = how-to, 3 = comparison. */
  tier: number;
  /** Where this question came from; null when the run did not record it. */
  source?: PromptSource | null;
  /**
   * One entry per engine, aligned to `matrix.engines`. `null` means we could
   * not measure that cell — an engine error, or a row where the literal check
   * and the classifier disagreed. Render it as unknown, never as absent.
   */
  cells: (MentionRole | null)[];
}

export interface MentionCompetitor {
  name: string;
  times_named: number;
}

export interface MentionCheck {
  /**
   * ready      — a completed run
   * none       — no run for this customer yet
   * unavailable— a run happened but every engine failed; NOT a score of zero
   * running    — a check is in flight RIGHT NOW. Server-side state, so it
   *              survives navigating away and reloading; the previous score
   *              (if any) still comes with it so the page is not blank.
   */
  status: "ready" | "none" | "unavailable" | "running";
  runAt?: string;
  business?: { name?: string; city?: string; category?: string; website?: string };
  score?: number;
  /** null on a first run — "nothing to compare against", not "no change". */
  scoreDelta?: number | null;
  appearedIn?: number;
  totalQuestions?: number;
  enginesChecked?: string[];
  /** Where the buyer questions came from; see PromptSource. */
  promptSource?: PromptSource;
  /** Why they are not real Search Console questions; see PromptSourceReason. */
  promptSourceReason?: PromptSourceReason | "" | null;
  /** "scheduled" — the automatic re-check (SGL-139); "manual" — Run check. */
  trigger?: "scheduled" | "manual";
  /**
   * A check newer than this result that did not produce one (SGL-139): it
   * failed part-way, or a scheduled check was skipped (no credit, cap reached,
   * missing city...). null when the result shown IS the latest attempt.
   */
  lastAttempt?: LastAttempt | null;
  competitors?: MentionCompetitor[];
  citedSources?: CitedSource[];
  gaps?: string[];
  matrix?: { engines: string[]; rows: MentionMatrixRow[] };
  /**
   * What the Business Profile does not list, for the local questions this run
   * lost (SGL-328 AC2/AC4). `null`/absent means it was not computed for this
   * run (an older run, or the step failed) — never "checked, found nothing".
   */
  profileFindings?: ProfileFindings | null;
  /**
   * What each Business Profile change the owner applied looked like on this
   * check (SGL-328 #2). `null`/absent means it was not computed for this run —
   * never "no changes", which is status "no_edits".
   */
  profileEditOutcomes?: ProfileEditOutcomes | null;
  /** Engines asked this run that failed outright (SGL-510). */
  enginesFailed?: string[];
  /** Engines with no credential configured — "not set up", never "absent". */
  enginesUnconfigured?: string[];
  /** Claude answering with no search ("Claude knows you"). null = older run. */
  claudeMemory?: ClaudeMemory | null;
  /** Is the site in Brave's index (Claude's search). null = older run. */
  braveIndex?: BraveIndex | null;
}

/* ── Claude + Brave (SGL-510) ────────────────────────────────────────────
 * Mirrors chat-service _memory_view and geo ai_mentions/brave.index_check. */

export type EngineRunStatus = "ok" | "failed" | "unconfigured";

export interface ClaudeMemoryRow {
  question: string;
  tier: number;
  /** null — not measured (error or needs review), never an absence. */
  role: MentionRole | null;
  competitors: string[];
  error: string | null;
}

export interface ClaudeMemory {
  status: EngineRunStatus;
  engine?: string;
  model?: string;
  appeared_in?: number;
  scored_rows?: number;
  competitors?: MentionCompetitor[];
  rows?: ClaudeMemoryRow[];
}

export interface BraveQuestionRank {
  question: string;
  /** 1-based rank in Brave's top 10; null = not in the top 10. */
  rank: number | null;
  url: string | null;
  /** Set when the lookup failed — then rank says nothing. */
  error: string | null;
}

export interface BraveIndex {
  status: EngineRunStatus;
  domain?: string;
  indexed?: boolean;
  indexed_pages_seen?: number;
  indexed_pages_sample?: string[];
  questions_ranked?: number;
  questions_measured?: number;
  question_ranks?: BraveQuestionRank[];
  error?: string;
}

/* ── Business Profile edit outcomes (SGL-328 #2) ─────────────────────────── */

/** Mirrors geo_visibility/ai_mentions/edit_outcomes.py STATUSES. Every value
 *  but "ok" means "no outcomes, and here is why". */
export type ProfileEditOutcomesStatus =
  | "ok"
  | "no_edits"
  | "edit_log_failed"
  | "insufficient_coverage"
  | "not_a_customer";

/**
 * Where the change stands on Google now.
 *   not_on_google — it was sent (unconfirmed, or held for review) but has never
 *                   shown on the listing; distinct from changed_back, where it
 *                   was there and Google later replaced it.
 */
export type EditGoogleState =
  | "still_on_google"
  | "changed_back"
  | "not_on_google"
  | "pending_review"
  | "superseded"
  | "unknown";

/** Counts are AI ANSWERS (one question put to one engine), the dashboard's own
 *  unit, never questions. */
export interface EditNamedCounts {
  run_at?: string | null;
  answers_named: number | null;
  answers_checked: number | null;
}

export interface ProfileEditOutcome {
  location_id: string | null;
  field: string;
  applied_at: string;
  edit_status: string;
  old: unknown;
  new: unknown;
  questions: string[];
  result: "measured" | "too_soon" | "no_baseline";
  named_before: EditNamedCounts | null;
  named_after: EditNamedCounts | null;
  /** null when not measured, or when it could not be told. */
  comparable: boolean | null;
  not_comparable_reason: "prompt_set_changed" | "engine_set_changed" | "prompt_set_unknown" | null;
  questions_before_after: {
    question: string;
    found_before: boolean | null;
    found_after: boolean | null;
    named_before: EditNamedCounts | null;
    named_after: EditNamedCounts | null;
  }[];
  google_state: EditGoogleState;
  google_now: unknown;
}

export interface ProfileEditOutcomes {
  status: ProfileEditOutcomesStatus;
  outcomes: ProfileEditOutcome[];
  profile_status: string | null;
  location_id: string | null;
  log_truncated: boolean;
}

/* ── Business Profile findings (SGL-328 AC4) ─────────────────────────────── */

/**
 * Every status except "ok" means "no findings, and here is why".
 * "ok" with an empty `findings` list means we checked and found nothing.
 * Mirrors geo_visibility/ai_mentions/profile_findings.py STATUSES.
 */
export type ProfileFindingsStatus =
  | "ok"
  | "not_connected"
  | "needs_reauth"
  | "audit_failed"
  | "insufficient_coverage"
  | "no_local_lost_questions"
  | "matcher_failed"
  | "not_a_customer";

/** Inner fields are snake_case, as the backend sends them (like CitedSource). */
export interface ProfileFinding {
  code: "service_not_listed";
  /** The buyer question, verbatim from the mention check. */
  question: string;
  tier: number;
  /** The service the question asks for, in the question's own words. */
  service: string;
  /** engine id -> how the business appeared in that engine's answer. */
  outcomes: Record<string, MentionRole>;
  /** Businesses AI named for this question, most named first (at most 5). */
  competitors: string[];
  /** http(s) pages AI cited for this question, excluding the business's own site (at most 5). */
  cited_pages: string[];
  profile_checked: {
    services: string[];
    primary_category: string | null;
    additional_categories: string[];
  };
  /** Conditional, no-promise advice written by the backend. Shown verbatim. */
  expected_change: string;
}

export interface ProfileFindings {
  status: ProfileFindingsStatus;
  /** Always [] unless status is "ok". */
  findings: ProfileFinding[];
  /** 0 for every status except "ok". */
  questions_checked: number;
  location_id: string | null;
}

export async function fetchMentionCheck(
  customerId: string,
  idToken: string,
): Promise<MentionCheck> {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  const base = CHAT_API_BASE_URL.replace(/\/$/, "");
  const resp = await authedFetch(
    `${base}/visibility/mentions?customer_id=${encodeURIComponent(customerId)}`,
    {},
    idToken,
  );
  if (!resp.ok) {
    throw new Error(`Mention check API error (${resp.status})`);
  }
  return resp.json();
}

export interface RecordedGbpProposal {
  /** The thread the card was stored on; a new chat adopts it. */
  threadId: string | null;
  /** The card's row; passed back on every later change rewrites that row. */
  cardMessageSk: string | null;
}

/** POST /visibility/gbp-proposal (SGL-328): stores the Visibility Manager's
 *  Business Profile card on the conversation, so it reloads from History and the
 *  model reads it (`summary`) on the next turn. Writes nothing to Google. */
export async function recordGbpProposal(params: {
  customerId: string;
  agentId: string;
  /** The owner's question, recorded as the thread's first user turn. Omit (null) for a
   *  card the owner typed for: the thread already holds their own message (SGL-403). */
  question: string | null;
  proposal: Record<string, unknown>;
  summary: string;
  threadId?: string | null;
  cardMessageSk?: string | null;
}): Promise<RecordedGbpProposal> {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  const base = CHAT_API_BASE_URL.replace(/\/$/, "");
  const resp = await authedFetch(`${base}/visibility/gbp-proposal`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      customer_id: params.customerId,
      agent_id: params.agentId,
      question: params.question || undefined,
      proposal: params.proposal,
      summary: params.summary,
      thread_id: params.threadId || null,
      card_message_sk: params.cardMessageSk || null,
    }),
  });
  if (!resp.ok) {
    throw new Error(`Saving the Business Profile card failed (${resp.status})`);
  }
  const data = await resp.json();
  return {
    threadId: typeof data?.thread_id === "string" ? data.thread_id : null,
    cardMessageSk: typeof data?.card_message_sk === "string" ? data.card_message_sk : null,
  };
}

/** Where a suggested value came from (chat-service gbp_suggestions.py). */
export type GbpSuggestionSource = "brand_details" | "drafted" | "google_categories";

export interface GbpSuggestion {
  /** string, a list of names, or Google categories ({name, display_name}[]). */
  value: unknown;
  source: GbpSuggestionSource;
}

export interface GbpSuggestions {
  suggestions: Record<string, GbpSuggestion>;
  /** Gaps with no real source, and why ("no_source", "credits_exhausted", ...). */
  unavailable: Record<string, string>;
}

/** POST /visibility/gbp-suggestions (SGL-328): proposed values for empty profile
 *  fields, each from a real source. Writes nothing. */
export async function fetchGbpSuggestions(params: {
  customerId: string;
  agentId: string;
  locationId: string | null;
  gaps: string[];
  services: string[];
}): Promise<GbpSuggestions> {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  const base = CHAT_API_BASE_URL.replace(/\/$/, "");
  const resp = await authedFetch(`${base}/visibility/gbp-suggestions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      customer_id: params.customerId,
      agent_id: params.agentId,
      location_id: params.locationId,
      gaps: params.gaps,
      services: params.services,
    }),
  });
  if (!resp.ok) {
    throw new Error(`Business Profile suggestions failed (${resp.status})`);
  }
  const data = await resp.json();
  return {
    suggestions: data?.suggestions && typeof data.suggestions === "object" ? data.suggestions : {},
    unavailable: data?.unavailable && typeof data.unavailable === "object" ? data.unavailable : {},
  };
}

/** POST /visibility/gbp-plan (SGL-421): the full Business Profile proposal the
 *  Visibility Manager prepares, for the starter card. A typed request gets the
 *  same plan from the review tool's `gbp_plan` SSE event instead. Reads only.
 *  Returns the raw body; utils/gbpPlan.ts readPlan checks it. */
export async function fetchGbpPlan(params: { customerId: string; agentId: string }): Promise<unknown> {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  const base = CHAT_API_BASE_URL.replace(/\/$/, "");
  const resp = await authedFetch(`${base}/visibility/gbp-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_id: params.customerId, agent_id: params.agentId }),
  });
  if (!resp.ok) {
    throw new Error(`Business Profile proposal failed (${resp.status})`);
  }
  return resp.json();
}

export interface LastAttempt {
  status: "failed" | "skipped";
  /** Stable code — the dashboard picks the wording. */
  reason: string;
  message: string;
  trigger: "scheduled" | "manual";
  attemptedAt: string;
}

/* ── score history (SGL-88 / SGL-139) ───────────────────────────────────── */

export interface MentionHistoryPoint {
  runDate: string;
  /** null — the run could not measure (coverage floor); not a score of zero. */
  score: number | null;
  appearedIn?: number;
  totalQuestions?: number;
  enginesChecked: string[];
  promptSource: PromptSource;
  /** Fingerprint of the question set; null for runs from before it was recorded. */
  promptSetHash: string | null;
  /**
   * Whether this point is comparable with the previous measured point (same
   * questions and same engines).
   * false — something measured changed; see notComparableReason.
   * null  — the oldest point, or one that predates recording the questions.
   */
  comparableToPrevious: boolean | null;
  /** Why not comparable. Absent from backends older than the SGL-88 fixes. */
  notComparableReason?: "prompt_set_changed" | "prompt_set_unknown" | "engine_set_changed" | null;
  /** false — may have been scored with no location; exclude from a trend. */
  reliable: boolean;
  trigger: "scheduled" | "manual";
}

export interface MentionHistory {
  businessKey: string | null;
  businessKeys: string[];
  /** true — runs exist under more than one business key; this is only part. */
  truncated: boolean;
  /** Newest first. */
  series: MentionHistoryPoint[];
}

export async function fetchMentionHistory(
  customerId: string,
  idToken: string,
): Promise<MentionHistory> {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  const base = CHAT_API_BASE_URL.replace(/\/$/, "");
  const resp = await authedFetch(
    `${base}/visibility/mentions/history?customer_id=${encodeURIComponent(customerId)}`,
    {},
    idToken,
  );
  if (!resp.ok) {
    throw new Error(`Mention history API error (${resp.status})`);
  }
  return resp.json();
}

export interface CitedSource {
  domain: string;
  times_cited: number;
  is_your_site: boolean;
}

/** Raised when the brand profile doesn't say clearly enough what the business
 *  sells. The category drives every question the check asks, so we refuse
 *  rather than guess — the UI turns this into "tell us what you sell". */
export class InsufficientBusinessDetail extends Error {
  /** "location_unknown" when it is the city that is missing; otherwise unset. */
  reason?: string;
  constructor(message: string, reason?: string) {
    super(message);
    this.name = "InsufficientBusinessDetail";
    this.reason = reason;
  }
}

export async function startMentionCheck(
  customerId: string,
  idToken: string,
): Promise<{ status: "started" | "already_running" | "already_ran_today"; runDate?: string }> {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  const base = CHAT_API_BASE_URL.replace(/\/$/, "");
  const resp = await authedFetch(
    `${base}/visibility/mentions/run?customer_id=${encodeURIComponent(customerId)}`,
    { method: "POST" },
    idToken,
  );
  if (resp.status === 422) {
    const body = await resp.json().catch(() => ({}));
    throw new InsufficientBusinessDetail(
      body?.detail?.message ||
        "We need to know what your business sells before we can check how AI describes it.",
      body?.detail?.reason,
    );
  }
  if (!resp.ok) {
    throw new Error(`Could not start the check (${resp.status})`);
  }
  return resp.json();
}

/** The AI visibility check-up (SGL-507): free_runs_left free check-ups come
 *  first; after that one runs every 15 days only while `enabled` is on, billed
 *  at its real credit cost (~price_estimate dollars). Mirrors chat-service
 *  app/routes/agent_runs.py (_settings_response). */
export interface AuditSettings {
  agent_id: string;
  enabled: boolean;
  free_runs_left: number;
  price_estimate: string;
  cadence_days: number;
}

function auditSettingsUrl(): string {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  return `${CHAT_API_BASE_URL.replace(/\/$/, "")}/agent-runs/visibility-audit/settings`;
}

export async function fetchAuditSettings(customerId: string, idToken: string): Promise<AuditSettings> {
  const resp = await authedFetch(
    `${auditSettingsUrl()}?customer_id=${encodeURIComponent(customerId)}`,
    {},
    idToken,
  );
  if (!resp.ok) {
    throw new Error(`Check-up settings API error (${resp.status})`);
  }
  return resp.json();
}

export async function saveAuditSettings(
  customerId: string,
  enabled: boolean,
  idToken: string,
): Promise<AuditSettings> {
  const resp = await authedFetch(
    auditSettingsUrl(),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customer_id: customerId, enabled }),
    },
    idToken,
  );
  if (!resp.ok) {
    throw new Error(`Check-up settings API error (${resp.status})`);
  }
  return resp.json();
}
